import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const ActiveVehiclesReport = ({ activeVehiclesData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'reg', label: 'Registration' },
        { key: 'hire_from', label: 'Hire From' },
        { key: 'hire_to', label: 'Hire To' },
        { key: 'service_provider', label: 'Service Provider' },
        { key: 'cost', label: 'Cost' },
        { key: 'spot', label: 'Spot' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                activeVehiclesData.length = 0; // Clear activeVehiclesData if password is invalid
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        setOpenRequestPassword(false); // Close the modal
    };
    
    const showData = () => {
        
        if (activeVehiclesData && activeVehiclesData.length > 0) {
            // Sort vehicles: units/rigids first (alphabetically), then trailers (alphabetically)
            const sortedData = [...activeVehiclesData].sort((a, b) => {
                const regA = a.reg?.toLowerCase() || '';
                const regB = b.reg?.toLowerCase() || '';
                
                // Check if registration starts with trailer patterns (T, TR, TRAIL)
                const isTrailerA = /^(t|tr|trail)/i.test(regA);
                const isTrailerB = /^(t|tr|trail)/i.test(regB);
                
                // If one is trailer and other is not, non-trailer comes first
                if (isTrailerA && !isTrailerB) return 1;
                if (!isTrailerA && isTrailerB) return -1;
                
                // Both are same type, sort alphabetically
                return regA.localeCompare(regB);
            });
            
            // Format the data for display
            const formatted = sortedData.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'hire_from' || key === 'hire_to') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj) || !entry[key]
                            ? '—'
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'cost') {
                        newEntry[key] = entry.cost ? `£${parseFloat(entry.cost).toFixed(2)}` : '—';
                    } else if (key === 'spot') {
                        newEntry[key] = entry.on_spot ? 'Yes' : 'No';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(formatted);
        }
    };

    const calculateSummaryStats = (data) => {
        const stats = {
            totalVehicles: data.length,
            spotHireCount: data.filter(item => item.spot === 'Yes').length,
            longTermCount: data.filter(item => item.spot === 'No').length,
            uniqueProviders: new Set(data.map(item => item.service_provider).filter(Boolean)).size,
        };
        setSummaryStats(stats);
    };

    const handleExportToCSV = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Password verification required to download data.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        if (tableData.length === 0) {
            showAlert({
                message: 'No data available to export.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        downloadCSV(tableData);
        
        showAlert({
            message: 'Active Vehicles Report exported successfully!',
            type: 'success',
            buttons: [{ type: 'success', label: 'Close' }],
        });
    };

    const handleSort = (columnKey) => {
        let direction = 'asc';
        if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        const sortedData = [...tableData].sort((a, b) => {
            let aVal = a[columnKey];
            let bVal = b[columnKey];
            
            // Handle special sorting for cost (remove £ symbol and convert to number)
            if (columnKey === 'cost') {
                aVal = parseFloat(aVal?.toString().replace('£', '') || '0');
                bVal = parseFloat(bVal?.toString().replace('£', '') || '0');
            }
            // Handle date sorting (convert DD-MM-YYYY to comparable format)
            else if (columnKey === 'hire_from' || columnKey === 'hire_to') {
                // Convert DD-MM-YYYY to YYYY-MM-DD for proper sorting, handle '—' as lowest value
                const convertDate = (dateStr) => {
                    if (!dateStr || dateStr === '—') return '1900-01-01';
                    const parts = dateStr.split('-');
                    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
                    return dateStr;
                };
                aVal = convertDate(aVal);
                bVal = convertDate(bVal);
            }
            // Handle text/string sorting (case insensitive)
            else {
                aVal = aVal?.toString().toLowerCase() || '';
                bVal = bVal?.toString().toLowerCase() || '';
            }
            
            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        setTableData(sortedData);
        setSortConfig({ key: columnKey, direction });
    };

    const getSortIcon = (columnKey) => {
        // Remove sort arrows - just return empty string
        return '';
    };

    const styles = {
        container: {
            padding: '20px',
            backgroundColor: isLightTheme ? '#ffffff' : '#2d3748',
            color: isLightTheme ? '#333333' : '#ffffff',
            borderRadius: '8px',
            boxShadow: isLightTheme 
                ? '0 2px 4px rgba(0,0,0,0.1)' 
                : '0 2px 4px rgba(0,0,0,0.3)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '10px'
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            margin: 0,
            color: isLightTheme ? '#1a202c' : '#ffffff'
        },
        summaryGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
        },
        summaryCard: {
            padding: '15px',
            backgroundColor: isLightTheme ? '#f7fafc' : '#4a5568',
            borderRadius: '6px',
            border: `1px solid ${isLightTheme ? '#e2e8f0' : '#2d3748'}`,
            textAlign: 'center'
        },
        summaryValue: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: isLightTheme ? '#2b6cb0' : '#90cdf4',
            margin: '0 0 5px 0'
        },
        summaryLabel: {
            fontSize: '14px',
            color: isLightTheme ? '#4a5568' : '#a0aec0',
            margin: 0
        },
        tableContainer: {
            backgroundColor: isLightTheme ? '#ffffff' : '#1a202c',
            borderRadius: '6px',
            overflow: 'hidden',
            border: `1px solid ${isLightTheme ? '#e2e8f0' : '#4a5568'}`,
            boxShadow: isLightTheme 
                ? '0 1px 3px rgba(0,0,0,0.1)' 
                : '0 1px 3px rgba(0,0,0,0.2)'
        },
        table: {
            width: '740px',
            borderCollapse: 'collapse',
            tableLayout: 'fixed'
        },
        tableHeader: {
            backgroundColor: isLightTheme ? '#f7fafc' : '#2d3748',
            borderBottom: `2px solid ${isLightTheme ? '#e2e8f0' : '#4a5568'}`
        },
        headerCell: {
            padding: '12px',
            textAlign: 'left',
            fontWeight: '600',
            fontSize: '14px',
            color: isLightTheme ? '#2d3748' : '#ffffff',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 0.2s ease',
            boxSizing: 'border-box'
        },
        tableRow: {
            borderBottom: `1px solid ${isLightTheme ? '#f1f5f9' : '#4a5568'}`
        },
        tableCell: {
            padding: '12px',
            fontSize: '14px',
            color: isLightTheme ? '#4a5568' : '#e2e8f0',
            boxSizing: 'border-box'
        },
        noDataContainer: {
            textAlign: 'center',
            padding: '40px 20px',
            color: isLightTheme ? '#718096' : '#a0aec0'
        },
        noDataText: {
            fontSize: '16px',
            margin: 0
        }
    };

    if (!allowDownload && !openRequestPassword) {
        return (
            <div style={styles.container}>
                <div style={styles.noDataContainer}>
                    <p style={styles.noDataText}>No active vehicles data available for the selected date range.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {tableData && tableData.length > 0 && allowDownload ? (
                <>
                    {/* Summary Statistics */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">🚛 Active Vehicles Summary</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <strong>Total Vehicles:</strong> {summaryStats.totalVehicles || 0}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Spot Hire:</strong> {summaryStats.spotHireCount || 0}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Long Term:</strong> {summaryStats.longTermCount || 0}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Service Providers:</strong> {summaryStats.uniqueProviders || 0}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-12 text-end">
                                            <Button
                                                onClick={handleExportToCSV}
                                                className="btn btn-success btn-sm"
                                            >
                                                Export CSV
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Active Vehicles Data ({tableData.length} vehicles)</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'} p-0`}>
                                    <div className="table-responsive">
                                        <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                            <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                                                <tr>
                                                    {displayColumns.map((column) => (
                                                        <th 
                                                            key={column.key} 
                                                            className="text-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSort(column.key)}
                                                            title={`Click to sort by ${column.label}`}
                                                        >
                                                            {column.label}{getSortIcon(column.key)}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.map((row, index) => (
                                                    <tr key={index}>
                                                        {displayColumns.map((column) => (
                                                            <td key={column.key} className="text-center">
                                                                {row[column.key]}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                !openRequestPassword && (
                    <div className={`alert ${isLightTheme ? 'alert-info' : 'alert-dark'} text-center`}>
                        <h5>No active vehicles data available for the selected date range.</h5>
                    </div>
                )
            )}
        </div>
    );
};

export default ActiveVehiclesReport;